import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
// Import all the firestore functions we need
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  doc, 
  updateDoc,
  getDoc
} from 'firebase/firestore'; 
import { useAppContext } from '../App';
import { Calendar, Clock, MapPin, Loader, AlertCircle, MessageSquare, Check, X } from 'lucide-react';
import ReviewModal from './ReviewModal'; // Import the new modal
import { useTranslation } from 'react-i18next';
import { 
  getPolicyBadge, 
  getCancellationPolicyStatus,
  formatTimeRemaining,
  calculateHoursUntilBooking
} from '../utils/cancellationPolicy';

// --- BookingCard Component ---
const BookingCard = ({ booking, onLeaveReview, onCancelBooking, isPast, t, language, stadiumPolicy }) => {
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
  const [isCancelling, setIsCancelling] = React.useState(false);
  
  const date = new Date(booking.date + 'T00:00:00');
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Calculate cancellation policy status with stadium-specific policy
  const policyStatus = getCancellationPolicyStatus(booking.date, booking.startTime, stadiumPolicy);
  const policyBadge = getPolicyBadge(booking.date, booking.startTime, booking.price, language, stadiumPolicy);
  const hoursUntil = calculateHoursUntilBooking(booking.date, booking.startTime);
  const timeRemaining = formatTimeRemaining(hoursUntil, language);

  const handleCancelClick = async () => {
    setIsCancelling(true);
    try {
      await onCancelBooking(booking);
      setShowCancelConfirm(false);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert(language === 'ar' ? 'حدث خطأ أثناء إلغاء الحجز' : 'Error cancelling booking');
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg overflow-hidden">
      <div className="flex flex-col md:flex-row">
        <div className="p-6 flex-1">
          <p className="text-sm font-semibold text-primary">{formattedDate}</p>
          <h3 className="text-2xl font-bold text-neutral-800 dark:text-white mt-1">{booking.stadiumName}</h3>
          <p className="text-lg text-neutral-600 dark:text-neutral-400 font-medium">{booking.sportName}</p>
          
          <div className="flex items-center gap-6 mt-4 text-neutral-600 dark:text-neutral-400">
            <div className="flex items-center gap-2">
              <Clock size={16} />
              <span>{booking.startTime} - {booking.endTime}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={16} />
              <span>{booking.customerName}</span>
            </div>
            {/* Show Payment Status Badge */}
            <div className={`flex items-center gap-2 font-medium ${booking.paymentType === 'online' ? 'text-green-600' : 'text-amber-600'}`}>
                {booking.paymentType === 'online' ? (
                    <span className="px-2 py-1 bg-green-100 rounded text-xs">{language === 'ar' ? 'مدفوع اونلاين' : 'Paid Online'}</span>
                ) : (
                    <span className="px-2 py-1 bg-amber-100 rounded text-xs">{language === 'ar' ? 'الدفع في الملعب' : 'Pay at Venue'}</span>
                )}
            </div>
          </div>

          {/* Cancellation Policy Badge - Only for Upcoming Bookings */}
          {!isPast && (
            <div className="mt-4 space-y-3">
              {/* Main Policy Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 ${policyBadge.bgColor} ${policyBadge.textColor} ${policyBadge.borderColor} font-semibold text-sm shadow-md`}>
                <span className="text-lg">{policyBadge.icon}</span>
                <span>{policyBadge.text}</span>
              </div>
              
              {/* Policy Type Indicator */}
              {stadiumPolicy && (
                <div className="flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-400">
                  <Clock size={14} />
                  <span>
                    {stadiumPolicy.type === 'full-payment' 
                      ? (language === 'ar' ? 'دفع كامل مقدماً' : 'Full Payment Required')
                      : (language === 'ar' ? 'الدفع في الملعب' : 'Pay at Venue')
                    }
                  </span>
                  <span className="mx-1">•</span>
                  <span>
                    {language === 'ar' 
                      ? `نافذة الإلغاء: ${stadiumPolicy.cancellationWindow} ساعة`
                      : `${stadiumPolicy.cancellationWindow}h window`
                    }
                  </span>
                </div>
              )}
              
              {/* Time Remaining Countdown */}
              {policyStatus.status !== 'past' && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
                    {policyStatus.status === 'free' || policyStatus.status === 'full-refund' ? (
                      <>
                        {language === 'ar' ? '⏰ متبقي للإلغاء المجاني:' : '⏰ Free cancellation ends in:'}
                        <span className="font-bold text-green-600 dark:text-green-400 ml-1 mr-1">
                          {timeRemaining}
                        </span>
                      </>
                    ) : (
                      <>
                        {language === 'ar' ? '⏰ الوقت حتى الحجز:' : '⏰ Time until booking:'}
                        <span className="font-bold text-orange-600 dark:text-orange-400 ml-1 mr-1">
                          {timeRemaining}
                        </span>
                      </>
                    )}
                  </p>
                  
                  {/* Refund/Fee Information */}
                  {policyStatus.status === 'full-refund' && (
                    <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                      <Check size={12} />
                      {language === 'ar' 
                        ? `استرداد ${stadiumPolicy?.refundPercentage || 100}% متاح`
                        : `${stadiumPolicy?.refundPercentage || 100}% refund available`
                      }
                    </p>
                  )}
                  
                  {policyStatus.status === 'no-refund' && (
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <X size={12} />
                      {language === 'ar' 
                        ? 'لا يوجد استرداد - خارج نافذة الإلغاء'
                        : 'No refund - outside cancellation window'
                      }
                    </p>
                  )}
                  
                  {policyStatus.status === 'fee-required' && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {language === 'ar' 
                        ? `رسوم إلغاء ${stadiumPolicy?.lateCancellationFee || 50}% قد تطبق`
                        : `${stadiumPolicy?.lateCancellationFee || 50}% cancellation fee may apply`
                      }
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="bg-primary/10 p-6 flex flex-col justify-center items-center md:w-48">
          <p className="text-sm text-primary-dark">{t('mybookings.totalPaid')}</p>
          <p className="text-4xl font-bold text-primary">${booking.price}</p>
        </div>
      </div>
      
      {/* Cancel Button - Only for upcoming bookings with free cancellation or full refund */}
      {!isPast && (policyStatus.status === 'free' || policyStatus.status === 'full-refund') && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-6 py-4">
          {!showCancelConfirm ? (
            <button 
              onClick={() => setShowCancelConfirm(true)}
              className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition"
            >
              <X size={16} />
              {language === 'ar' ? 'إلغاء الحجز' : 'Cancel Booking'}
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-neutral-700 dark:text-neutral-300 font-semibold">
                {language === 'ar' ? 'هل أنت متأكد من إلغاء هذا الحجز؟' : 'Are you sure you want to cancel this booking?'}
              </p>
              {policyStatus.status === 'full-refund' && (
                <p className="text-xs text-green-600 dark:text-green-400">
                  {language === 'ar' 
                    ? `✓ سيتم استرداد كامل المبلغ (${stadiumPolicy?.refundPercentage || 100}%)`
                    : `✓ Full refund will be processed (${stadiumPolicy?.refundPercentage || 100}%)`
                  }
                </p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelClick}
                  disabled={isCancelling}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isCancelling ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      {language === 'ar' ? 'جاري الإلغاء...' : 'Cancelling...'}
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      {language === 'ar' ? 'نعم، إلغاء' : 'Yes, Cancel'}
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={isCancelling}
                  className="px-4 py-2 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-800 dark:text-neutral-200 rounded-lg font-semibold text-sm transition disabled:opacity-50"
                >
                  {language === 'ar' ? 'لا، احتفظ بالحجز' : 'No, Keep Booking'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Warning for full-payment bookings with no refund */}
      {!isPast && policyStatus.status === 'no-refund' && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 bg-red-50 dark:bg-red-900/10 px-6 py-4">
          <p className="text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
            <AlertCircle size={16} />
            {language === 'ar' 
              ? `⚠️ خارج نافذة الإلغاء (${stadiumPolicy?.cancellationWindow || 36} ساعة). لا يوجد استرداد متاح. يرجى الاتصال بالملعب إذا لزم الأمر.`
              : `⚠️ Outside ${stadiumPolicy?.cancellationWindow || 36}h cancellation window. No refund available. Please contact the stadium if needed.`
            }
          </p>
        </div>
      )}
      
      {/* Warning for pay-at-venue bookings requiring contact */}
      {!isPast && policyStatus.status === 'fee-required' && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 bg-orange-50 dark:bg-orange-900/10 px-6 py-4">
          <p className="text-sm text-orange-700 dark:text-orange-400 flex items-center gap-2">
            <AlertCircle size={16} />
            {language === 'ar' 
              ? `⚠️ للإلغاء ضمن ${stadiumPolicy?.cancellationWindow || 36} ساعة، يرجى الاتصال بالملعب مباشرة. رسوم إلغاء ${stadiumPolicy?.lateCancellationFee || 50}% قد تطبق.`
              : `⚠️ To cancel within ${stadiumPolicy?.cancellationWindow || 36} hours, please contact the stadium directly. ${stadiumPolicy?.lateCancellationFee || 50}% cancellation fee may apply.`
            }
          </p>
        </div>
      )}
      
      {isPast && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-6 py-4">
          {booking.reviewed ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
              <Check size={16} className="text-primary" /> {t('mybookings.reviewSubmitted')}
            </p>
          ) : (
            <button 
              onClick={() => onLeaveReview(booking)}
              className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-dark dark:hover:text-primary-light transition"
            >
              <MessageSquare size={16} />
              {t('mybookings.leaveReview')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// --- MyBookings Page ---
const MyBookings = () => {
  const { user, loading: authLoading } = useAppContext();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stadiumPolicies, setStadiumPolicies] = useState({}); // Cache for stadium policies
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Fetch stadium cancellation policy
  const fetchStadiumPolicy = async (stadiumId) => {
    // Check cache first
    if (stadiumPolicies[stadiumId]) {
      return stadiumPolicies[stadiumId];
    }

    try {
      const stadiumDoc = await getDoc(doc(db, 'stadiums', stadiumId));
      if (stadiumDoc.exists()) {
        const policy = stadiumDoc.data().cancellationPolicy || null;
        // Cache the policy
        setStadiumPolicies(prev => ({
          ...prev,
          [stadiumId]: policy
        }));
        return policy;
      }
    } catch (error) {
      console.error('Error fetching stadium policy:', error);
    }
    return null;
  };

  const fetchBookings = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const bookingsRef = collection(db, 'bookings');
      const q = query(
        bookingsRef,
        where('userId', '==', user.uid)
      );

      const querySnapshot = await getDocs(q);
      const userBookings = [];
      querySnapshot.forEach((doc) => {
        userBookings.push({ id: doc.id, ...doc.data() });
      });
      
      userBookings.sort((a, b) => b.date.localeCompare(a.date));
      
      setBookings(userBookings);

      // Fetch policies for all stadiums in bookings
      const uniqueStadiumIds = [...new Set(userBookings.map(b => b.stadiumId))];
      await Promise.all(uniqueStadiumIds.map(id => fetchStadiumPolicy(id)));
      
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError(t('mybookings.error'));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const handleOpenReviewModal = (booking) => {
    setSelectedBooking(booking);
  };

  const handleCloseReviewModal = () => {
    setSelectedBooking(null);
  };

  // --- THIS IS THE CORRECTED FUNCTION ---
  const handleSubmitReview = async (reviewData) => {
    if (!selectedBooking || !user) {
      throw new Error("No user or booking selected");
    }

    // 1. Create the new review document
    await addDoc(collection(db, 'reviews'), {
      rating: reviewData.rating,
      comment: reviewData.comment,
      createdAt: new Date().toISOString(),
      bookingId: selectedBooking.id,
      stadiumId: selectedBooking.stadiumId,
      stadiumName: selectedBooking.stadiumName,
      sportName: selectedBooking.sportName,
      userId: user.uid,
      userName: user.name || user.displayName || 'Anonymous',
      userPhotoURL: user.photoURL || null,
    });

    // 2. Mark the booking as "reviewed"
    const bookingDocRef = doc(db, 'bookings', selectedBooking.id);
    await updateDoc(bookingDocRef, {
      reviewed: true
    });

    // 3. Get a reference to the stadium
    const stadiumRef = doc(db, 'stadiums', selectedBooking.stadiumId);

    // 4. Get all reviews for THAT stadium
    const reviewsQuery = query(
      collection(db, 'reviews'),
      where('stadiumId', '==', selectedBooking.stadiumId)
    );
    const reviewsSnapshot = await getDocs(reviewsQuery);

    if (!reviewsSnapshot.empty) {
      // 5. Calculate the new average and count
      let totalRating = 0;
      reviewsSnapshot.forEach((doc) => {
        totalRating += doc.data().rating;
      });
      const reviewCount = reviewsSnapshot.size;
      const newAverageRating = parseFloat((totalRating / reviewCount).toFixed(1));

      // 6. Update the stadium document
      await updateDoc(stadiumRef, {
        rating: newAverageRating,
        reviewCount: reviewCount
      });
    }

    // 7. Refresh the local bookings list to show "Review Submitted"
    fetchBookings(); 
  };
  // --- END CORRECTED FUNCTION ---

  // --- CANCEL BOOKING FUNCTION ---
  const handleCancelBooking = async (booking) => {
    if (!booking || !user) {
      throw new Error("No user or booking selected");
    }

    // Delete the booking document from Firestore
    const bookingDocRef = doc(db, 'bookings', booking.id);
    await updateDoc(bookingDocRef, {
      status: 'cancelled',
      cancelledAt: new Date().toISOString(),
      cancelledBy: user.uid
    });

    // Refresh the bookings list
    fetchBookings();
  };
  // --- END CANCEL BOOKING FUNCTION ---

  const today = new Date().toISOString().split('T')[0];
  const upcomingBookings = bookings.filter(b => b.date >= today && b.status !== 'cancelled');
  const pastBookings = bookings.filter(b => b.date < today && b.status !== 'cancelled');

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center">
          <Loader size={48} className="text-primary animate-spin" />
          <p className="text-neutral-600 dark:text-neutral-400 mt-4">{t('mybookings.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-neutral-100 dark:bg-neutral-900 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Global Header is already here */}
      
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-4xl font-bold text-neutral-800 dark:text-white mb-8">{t('mybookings.title')}</h1>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-neutral-700 dark:text-neutral-300 mb-4 pb-2 border-b-2 dark:border-neutral-700">{t('mybookings.upcoming')}</h2>
          {upcomingBookings.length > 0 ? (
            <div className="space-y-6">
              {upcomingBookings.map(booking => (
                <BookingCard 
                  key={booking.id} 
                  booking={booking} 
                  isPast={false} 
                  t={t} 
                  language={i18n.language}
                  onCancelBooking={handleCancelBooking}
                  stadiumPolicy={stadiumPolicies[booking.stadiumId]}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-white dark:bg-neutral-800 rounded-2xl shadow-lg">
              <Calendar size={48} className="mx-auto text-neutral-300 dark:text-neutral-600" />
              <p className="mt-4 text-neutral-500 dark:text-neutral-400">{t('mybookings.noUpcoming')}</p>
            </div>
          )}
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-neutral-700 dark:text-neutral-300 mb-4 pb-2 border-b-2 dark:border-neutral-700">{t('mybookings.past')}</h2>
          {pastBookings.length > 0 ? (
            <div className="space-y-6">
              {pastBookings.map(booking => (
                <BookingCard 
                  key={booking.id} 
                  booking={booking} 
                  isPast={true} 
                  onLeaveReview={handleOpenReviewModal}
                  t={t}
                  language={i18n.language}
                  onCancelBooking={handleCancelBooking}
                  stadiumPolicy={stadiumPolicies[booking.stadiumId]}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-white dark:bg-neutral-800 rounded-2xl shadow-lg">
              <AlertCircle size={48} className="mx-auto text-neutral-300 dark:text-neutral-600" />
              <p className="mt-4 text-neutral-500 dark:text-neutral-400">{t('mybookings.noPast')}</p>
            </div>
          )}
        </section>
      </div>
      {/* ... Review Modal ... */}
      {/* --- ADD THE MISSING CODE HERE --- */}
      {selectedBooking && (
        <ReviewModal
          booking={selectedBooking}
          onClose={handleCloseReviewModal}
          onSubmitReview={handleSubmitReview}
        />
      )}
      {/* --- END OF MISSING CODE --- */}
    </div>
  );
};

export default MyBookings;